import { yupResolver } from '@hookform/resolvers/yup';
import AddCircleOutline from '@mui/icons-material/AddCircleOutline';
import Close from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import {
  AddOnRateType,
  FuelSurchargeScheduleType,
  getV1CompaniesCompanyIdJobsJobIdLoads,
} from '@treadinc/horizon-api-spec';
import dayjs from 'dayjs';
import { t } from 'i18next';
import { isNil } from 'lodash-es';
import { observer } from 'mobx-react-lite';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo } from 'react';
import { useFieldArray, useForm, useWatch } from 'react-hook-form';
import * as yup from 'yup';

import { AutocompleteFormField } from '~components/FormFields/AutocompleteFormField';
import { TextFormField } from '~components/FormFields/TextFormField';
import { AddOn, AddOnCharge } from '~hooks/useAddOns/models';
import useAddOns from '~hooks/useAddOns/useAddOns';
import { Invoice } from '~hooks/useInvoices/models';
import { Load } from '~hooks/useLoad/models';
import { Settlement } from '~hooks/useSettlements/models';
import { DriverPayRowDef } from '~pages/Approvals/DriverPay/driversPayDataGridColumnsDefinition';
import { useStores } from '~store';
import { usePrevious } from '~utils/hooks/usePrevious';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';
import { formatPercentage } from '~utils/utilFunctions';

import {
  LineItemsTableCell,
  LineItemsTableTableRow,
} from './components/lineItemsTableComponents';
import {
  AddOnChargeDTO,
  addOnChargeSchema,
  appendAddOnRateTypeToName,
  invoiceHelpers,
  isLineEditable,
  schemaHelpers,
} from './helpers';

interface AddOnChargesTableRowsProps {
  addOnCharges: AddOnCharge[];
  invoice: Invoice | Settlement;
  isEditing?: boolean;
  isUpdating?: boolean;
  lineItemColSpan?: number;
  lineItemsTableColumnsCount: number;
  utils: DriverPayRowDef['row']['utils'];
}

const ADD_ON_CHARGES_TABLE_COLUMNS_COUNT = 4;

const addOnChargesSchema = yup.object().shape({
  addOnCharges: yup.array().of(addOnChargeSchema),
});

export type AddOnChargesDTO = yup.InferType<typeof addOnChargesSchema>;

export type AddOnSelectOption = {
  id: string;
  name: string;
};

export type AddOnChargesFormHandler = {
  onSubmit?: () => Promise<AddOnChargeDTO[]>;
  append?: () => void;
};

const AddOnChargesTableRows = forwardRef<
  AddOnChargesFormHandler,
  AddOnChargesTableRowsProps
>(function AddOnChargesTableRows(
  {
    addOnCharges,
    invoice,
    isEditing,
    isUpdating,
    lineItemColSpan = 2,
    lineItemsTableColumnsCount,
    utils,
  },
  ref,
) {
  const { addOnsStore, invoiceLineItemsStore, userStore } = useStores();
  const { getFuelSurchargeRate } = useAddOns();
  const companyId = getEffectiveUserCompanyId(userStore);

  const invoiceId = String(invoice.id);
  const lineItemsSubtotal = invoiceLineItemsStore.invoiceSubtotalByInvoiceId[invoiceId];
  const freightTotal = invoiceLineItemsStore.freightTotalByInvoiceId[invoiceId];
  const freightQuantity = invoiceLineItemsStore.freightQuantityByInvoiceId[invoiceId];
  const lineItemsHasEdits =
    invoiceLineItemsStore.lineItemsHasEditsByInvoiceId[invoiceId] ?? false;

  const originalAddOnChargesById = useMemo(
    () => new Map(addOnCharges.map((charge) => [charge.id, charge])),
    [addOnCharges],
  );

  const lockedAddOnChargeIds = useMemo(
    () =>
      new Set(
        addOnCharges
          .filter((addOnCharge) => !isLineEditable(addOnCharge, invoice))
          .map((addOnCharge) => addOnCharge.id),
      ),
    [addOnCharges, invoice],
  );

  const { addOnSelectOptions, addOnsById } = useMemo(() => {
    const selectableAddOns = addOnsStore.selectableAddOnsByInvoiceId[invoiceId] ?? [];

    const [options, indexed] = selectableAddOns.reduce(
      ([options, indexed], addOn) => {
        indexed[String(addOn.id)] = addOn;
        options.push({
          id: String(addOn.id),
          name: appendAddOnRateTypeToName(addOn.name, addOn.revenueCostRateType),
        });

        return [options, indexed];
      },
      [[] as AddOnSelectOption[], {} as Record<string, AddOn>],
    );

    return {
      addOnSelectOptions: options,
      addOnsById: indexed,
    };
  }, [addOnsStore.selectableAddOnsByInvoiceId[invoiceId]]);

  const {
    clearErrors,
    control,
    formState: { errors },
    handleSubmit,
  } = useForm<AddOnChargesDTO>({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    resolver: yupResolver(addOnChargesSchema),
    defaultValues: {
      addOnCharges: addOnCharges.map((addOnCharge) => {
        return schemaHelpers.addOnChargeToAddOnChargeDTO(addOnCharge);
      }),
    },
  });

  const { fields, append, update } = useFieldArray({
    control,
    name: 'addOnCharges',
  });
  const watchedAddOnCharges = useWatch({ control, name: 'addOnCharges' });
  const previousWatchedAddOnCharges = usePrevious(watchedAddOnCharges);

  const handleAddAddOnClick = useCallback(() => {
    const addOnCharge = new AddOnCharge(
      null,
      '',
      0,
      0,
      0,
      0,
      AddOnRateType.RATE_FOR_EACH,
      null,
      { id: '' },
      null,
      null,
      null,
      null,
      null,
    );

    append(schemaHelpers.addOnChargeToAddOnChargeDTO(addOnCharge));
  }, [append]);

  const handleRemoveAddOnClick = useCallback(
    (index: number) => {
      const addOnChargeToDelete = watchedAddOnCharges?.[index];

      if (addOnChargeToDelete) {
        update(index, { ...addOnChargeToDelete, _destroy: 1 });
      }
    },
    [update, watchedAddOnCharges],
  );

  // This is to pre-fill the add-on charge DTO based on the selected add-on
  useEffect(() => {
    if (!watchedAddOnCharges || !previousWatchedAddOnCharges) {
      return;
    }

    watchedAddOnCharges.forEach(async (current, index) => {
      const currentAddOnId = current.addOn?.id;
      const previousAddOnIn = previousWatchedAddOnCharges[index]?.addOn?.id;
      const selectedAddOnDidChange = currentAddOnId && currentAddOnId !== previousAddOnIn;

      // If the selected add-on did change then use the newly selected add-on values
      // As the default values for the add-on charge DTO
      if (selectedAddOnDidChange) {
        const newlySelectedAddOn = addOnsById[currentAddOnId];
        let loads: Load[] = [];

        // Get the loads for the job
        if (
          newlySelectedAddOn.revenueCostRateType === AddOnRateType.PER_LOAD &&
          !current.load?.id
        ) {
          if (invoice instanceof Invoice) {
            if (invoice.job?.id) {
              const loadsResponse = await getV1CompaniesCompanyIdJobsJobIdLoads({
                path: { 'company-id': companyId, 'job-id': invoice.job?.id },
              });
              loads = loadsResponse.data.data.map((load) => Load.parse(load));
            }
          }
        }

        // Handle fuel surcharge add-ons by fetching rate from index
        let percentage = newlySelectedAddOn.percentage;
        let quantity = newlySelectedAddOn.quantity;
        let rate = newlySelectedAddOn.rate;
        let surchargeType: FuelSurchargeScheduleType | null = null;

        if (newlySelectedAddOn.revenueCostRateType === AddOnRateType.FUEL_SURCHARGE) {
          // Get job start date from invoice for fuel surcharge lookup
          const jobStartDate =
            invoice instanceof Invoice && invoice.job?.jobStartAt
              ? dayjs.tz(invoice.job.jobStartAt).format('YYYY-MM-DD')
              : undefined;

          // Fetch fuel surcharge rate from the index based on job start date
          if (companyId) {
            const fuelSurchargeRate = await getFuelSurchargeRate(
              companyId,
              currentAddOnId,
              jobStartDate,
            );

            if (fuelSurchargeRate) {
              const isPercentageBased = !isNil(fuelSurchargeRate.surcharge_percentage);
              surchargeType = fuelSurchargeRate.surcharge_type;

              if (isPercentageBased) {
                percentage = Number(fuelSurchargeRate.surcharge_percentage);
                quantity = 0;
                rate = 0;
              } else {
                percentage = 0;
                quantity = 1;
                rate = Number(fuelSurchargeRate.surcharge ?? 0);
              }
            }
          }
        }

        const isNewlyCreatedPerLoadAddOnCharge =
          newlySelectedAddOn.revenueCostRateType === AddOnRateType.PER_LOAD &&
          loads.length > 0;

        const loadDTO = isNewlyCreatedPerLoadAddOnCharge
          ? { id: loads[0].id }
          : current.load?.id
            ? { id: current.load?.id }
            : null;

        const addOnCharge = new AddOnCharge(
          current.id,
          isNewlyCreatedPerLoadAddOnCharge && !current.load?.id
            ? `${newlySelectedAddOn.name}: Load ${loads[0].ordinality}`
            : (current.addOn.name ?? newlySelectedAddOn.name),
          percentage,
          quantity,
          rate,
          0,
          newlySelectedAddOn.revenueCostRateType,
          newlySelectedAddOn.externalId,
          { id: currentAddOnId },
          loadDTO,
          null,
          null,
          null,
          null,
          surchargeType,
        );

        update(index, schemaHelpers.addOnChargeToAddOnChargeDTO(addOnCharge));
        clearErrors([`addOnCharges.${index}.quantity`, `addOnCharges.${index}.value`]);

        // Append more charges for each load
        if (isNewlyCreatedPerLoadAddOnCharge && !current.load?.id && loads.length > 1) {
          for (let i = 1; i < loads.length; i++) {
            const additionalCharge = new AddOnCharge(
              null, // New charges don't have IDs yet
              `${newlySelectedAddOn.name}: Load ${loads[i].ordinality}`,
              newlySelectedAddOn.percentage,
              newlySelectedAddOn.quantity,
              newlySelectedAddOn.rate,
              1,
              newlySelectedAddOn.revenueCostRateType,
              newlySelectedAddOn.externalId,
              { id: currentAddOnId },
              { id: loads[i].id },
              null,
              null,
              null,
              null,
            );
            append(schemaHelpers.addOnChargeToAddOnChargeDTO(additionalCharge));
          }
        }
      }
    });
  }, [
    watchedAddOnCharges,
    previousWatchedAddOnCharges,
    update,
    clearErrors,
    addOnsById,
    append,
    companyId,
    getFuelSurchargeRate,
    invoice,
  ]);

  useEffect(() => {
    let total = 0;

    if (watchedAddOnCharges) {
      total = watchedAddOnCharges.reduce((sum, addOnCharge) => {
        const isDeleted = addOnCharge._destroy === 1;

        if (isDeleted) {
          return sum;
        }

        const original = originalAddOnChargesById.get(addOnCharge.id ?? null);
        const shouldRecompute = invoiceHelpers.shouldRecomputeAddOnChargeTotal(
          addOnCharge,
          original,
          lineItemsHasEdits,
        );

        const addOnChargeTotal = shouldRecompute
          ? invoiceHelpers.calculateAddOnChargeTotal(
              addOnCharge.addOnRateType
                ? (addOnCharge.addOnRateType as AddOnRateType)
                : null,
              addOnCharge.quantity,
              addOnCharge.value,
              addOnCharge.isPercentage ? addOnCharge.value : 0,
              lineItemsSubtotal,
              freightTotal,
              freightQuantity,
              addOnCharge.surchargeType,
            )
          : (original?.totalAmount ?? 0);

        return sum + addOnChargeTotal;
      }, 0);
    }

    addOnsStore.setAddOnChargesTotalByInvoiceId(invoiceId, total);
  }, [
    watchedAddOnCharges,
    lineItemsSubtotal,
    invoiceId,
    freightTotal,
    freightQuantity,
    lineItemsHasEdits,
    originalAddOnChargesById,
  ]);

  useImperativeHandle(
    ref,
    () => ({
      onSubmit: () => {
        return new Promise((resolve, reject) => {
          handleSubmit(
            (data) => {
              resolve(
                (data.addOnCharges ?? []).filter(
                  (addOnCharge) => !lockedAddOnChargeIds.has(addOnCharge.id ?? null),
                ),
              );
            },
            (errors) => {
              reject(errors);
            },
          )();
        });
      },
    }),
    [handleSubmit, lockedAddOnChargeIds],
  );

  const renderReadRow = (addOnCharge: AddOnCharge) => {
    const isFlatRateFuelSurcharge =
      addOnCharge.revenueCostRateType === AddOnRateType.FUEL_SURCHARGE &&
      !addOnCharge.percentage;
    const isNonRatePercentOfTotal =
      addOnCharge.revenueCostRateType === AddOnRateType.RATE_FOR_EACH ||
      addOnCharge.revenueCostRateType === AddOnRateType.PER_LOAD ||
      isFlatRateFuelSurcharge;

    const usesFreightQuantity =
      isFlatRateFuelSurcharge &&
      addOnCharge.surchargeType !== FuelSurchargeScheduleType.PER_LOAD;
    const displayQuantity = usesFreightQuantity
      ? (freightQuantity ?? addOnCharge.quantity)
      : addOnCharge.quantity;

    return (
      <LineItemsTableTableRow key={addOnCharge.id}>
        <LineItemsTableCell>
          {appendAddOnRateTypeToName(addOnCharge.name, addOnCharge.revenueCostRateType)}
        </LineItemsTableCell>

        <LineItemsTableCell
          colSpan={lineItemsTableColumnsCount - ADD_ON_CHARGES_TABLE_COLUMNS_COUNT}
        />

        <LineItemsTableCell textAlign="right">
          {isNonRatePercentOfTotal && displayQuantity}
        </LineItemsTableCell>

        <LineItemsTableCell textAlign="right">
          {isNonRatePercentOfTotal
            ? utils.currencyFormatter(addOnCharge.rate ?? 0)
            : formatPercentage(addOnCharge.percentage)}
        </LineItemsTableCell>

        <LineItemsTableCell textAlign="right">
          {utils.currencyFormatter(addOnCharge.totalAmount)}
        </LineItemsTableCell>
      </LineItemsTableTableRow>
    );
  };

  if (isEditing) {
    return (
      <>
        {fields.map((field, index) => {
          const isDeleted = watchedAddOnCharges?.[index]?._destroy === 1;

          if (isDeleted) {
            return null;
          }

          const editedAddOnCharge = watchedAddOnCharges?.[index];
          const original = originalAddOnChargesById.get(editedAddOnCharge?.id ?? null);

          if (original && lockedAddOnChargeIds.has(original.id)) {
            return renderReadRow(original);
          }

          const isAddOnSelected = Boolean(editedAddOnCharge?.addOn?.id);
          const isFuelSurcharge =
            editedAddOnCharge?.addOnRateType === AddOnRateType.FUEL_SURCHARGE;
          const isNonRatePercentOfTotal =
            editedAddOnCharge?.addOnRateType === AddOnRateType.RATE_FOR_EACH ||
            editedAddOnCharge?.addOnRateType === AddOnRateType.PER_LOAD;

          const shouldRecompute = invoiceHelpers.shouldRecomputeAddOnChargeTotal(
            editedAddOnCharge,
            original,
            lineItemsHasEdits,
          );

          const total = shouldRecompute
            ? invoiceHelpers.calculateAddOnChargeTotal(
                editedAddOnCharge?.addOnRateType
                  ? (editedAddOnCharge.addOnRateType as AddOnRateType)
                  : null,
                editedAddOnCharge?.quantity,
                editedAddOnCharge?.value,
                editedAddOnCharge?.isPercentage ? editedAddOnCharge?.value : 0,
                lineItemsSubtotal,
                freightTotal,
                freightQuantity,
                editedAddOnCharge?.surchargeType,
              )
            : (original?.totalAmount ?? 0);

          return (
            <LineItemsTableTableRow key={field.id}>
              <LineItemsTableCell colSpan={lineItemColSpan}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px',
                  }}
                >
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveAddOnClick(index)}
                    disabled={isUpdating}
                  >
                    <Close color="error" sx={{ fontSize: '.8rem' }} />
                  </IconButton>
                  <AutocompleteFormField
                    clearable
                    control={control}
                    errors={errors}
                    getLabel={(item) => item.name}
                    getValue={(item) => item.id}
                    isRequired
                    list={addOnSelectOptions}
                    name={`addOnCharges[${index}].addOn`}
                    sx={{
                      '& .MuiInputBase-input': { fontSize: '12px', p: '0 !important' },
                      '& .MuiAutocomplete-endAdornment': {
                        right: '5px !important',
                        top: '50%',
                        transform: 'translateY(-50%)',
                      },
                      '& .MuiSvgIcon-root': { width: '.75em', height: '.75em' },
                    }}
                    disabled={isUpdating}
                  />
                </Box>
              </LineItemsTableCell>
              <LineItemsTableCell
                colSpan={
                  lineItemsTableColumnsCount -
                  ADD_ON_CHARGES_TABLE_COLUMNS_COUNT -
                  lineItemColSpan +
                  1
                }
              />
              <LineItemsTableCell textAlign="right">
                {isAddOnSelected && isNonRatePercentOfTotal && (
                  <TextFormField
                    control={control}
                    errors={errors}
                    type="number"
                    name={`addOnCharges[${index}].quantity`}
                    sx={{
                      ml: 'auto',
                      '& .MuiFormControl-root': { mb: 0 },
                      '& .MuiOutlinedInput-root': { pr: 0 },
                      '& .MuiOutlinedInput-input': { p: '5px 7px', fontSize: '12px' },
                    }}
                    inputProps={{ sx: { textAlign: 'right' } }}
                    disabled={isUpdating}
                  />
                )}
              </LineItemsTableCell>
              <LineItemsTableCell textAlign="right">
                {isAddOnSelected ? (
                  <TextFormField
                    control={control}
                    errors={errors}
                    type="number"
                    name={`addOnCharges[${index}].value`}
                    sx={{
                      ml: 'auto',
                      '& .MuiFormControl-root': { mb: 0 },
                      '& .MuiOutlinedInput-root': { pr: 0, pl: '7px' },
                      '& .MuiOutlinedInput-input': {
                        p: '5px 7px 5px 0px',
                        fontSize: '12px',
                      },
                    }}
                    inputProps={{ sx: { textAlign: 'right' } }}
                    InputProps={{
                      startAdornment: (
                        <Typography
                          sx={{
                            mr: '5px',
                            fontSize: '12px',
                          }}
                        >
                          {isNonRatePercentOfTotal ||
                          (isFuelSurcharge && !editedAddOnCharge?.isPercentage)
                            ? '$'
                            : '%'}
                        </Typography>
                      ),
                    }}
                    disabled={isUpdating}
                  />
                ) : null}
              </LineItemsTableCell>
              <LineItemsTableCell textAlign="right">
                {isAddOnSelected && utils.currencyFormatter(total)}
              </LineItemsTableCell>
            </LineItemsTableTableRow>
          );
        })}
        <LineItemsTableTableRow>
          <LineItemsTableCell colSpan={lineItemsTableColumnsCount}>
            <Button
              color="primary"
              onClick={handleAddAddOnClick}
              startIcon={<AddCircleOutline />}
              disabled={isUpdating}
            >
              {t('approvals.driver_pay.tabs.driver_pay.add_add_on')}
            </Button>
          </LineItemsTableCell>
        </LineItemsTableTableRow>
      </>
    );
  }

  return addOnCharges.map(renderReadRow);
});

export default observer(AddOnChargesTableRows);
